import { Drawer } from "@xola/ui-kit";
import { DateFormat, isExperiencePurchaseItem } from "common";
import { isNumber } from "lodash";
import React from "react";
import { Permission, PurchaseItem, PurchaseItemObject, Timeslot } from "types";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useCollection } from "../../hooks/useCollection";
import { DashboardRoster } from "../Dashboard/Roster/DashboardRoster";
import { DrawerStackAnchor } from "@/components/Common/DrawerStackAnchor";
import { usePurchaseDrawerStore } from "@/stores/purchaseDrawerStore";

interface DashboardRosterDrawerProps {
    isOpen: boolean;
    timeslot?: Timeslot;
    onClose: () => void;
}

export const DashboardRosterDrawer = ({ isOpen, timeslot, onClose }: DashboardRosterDrawerProps) => {
    return (
        <Drawer
            size="2xl"
            isOpen={isOpen}
            content={<DrawerContent timeslot={timeslot} />}
            classNames={{ dialog: "z-20" }}
            onClose={onClose}
        />
    );
};

const DrawerContent = ({ timeslot }: { timeslot?: Timeslot }) => {
    const isPurchaseDrawerOpen = usePurchaseDrawerStore((state) => Boolean(state.purchaseId ?? state.purchaseItemId));

    if (!timeslot) {
        return null;
    }

    return (
        <div className="mx-3 -mt-4" id="roster-drawer">
            {isPurchaseDrawerOpen && <DrawerStackAnchor />}
            <DashboardRoster timeslot={timeslot} />
        </div>
    );
};

export const useTimeSlotFromItem = (item: PurchaseItem, isEnabled = false) => {
    const params = useTimeslotParams(item);

    const isExperience = isExperiencePurchaseItem(item);
    const isUnScheduled = item.object === PurchaseItemObject.ORDER;
    const isTimeslotsAvailable = isExperience && !isUnScheduled;

    const fetchParams = { staleTime: 2000, enabled: isTimeslotsAvailable && isEnabled };
    const { data: timeslots, isLoading } = useCollection<Timeslot>("timeslots", params, fetchParams);

    const timeslot = timeslots.find((timeslot) => timeslot.event?.id === item.event?.id);
    return { timeslot, isLoading };
};

const useTimeslotParams = (item: PurchaseItem) => {
    const { isGranted } = useAuthorization();
    const isUnScheduled = item.object === PurchaseItemObject.ORDER;
    if (!isExperiencePurchaseItem(item) || isUnScheduled) {
        return {};
    }

    const startDateFormat = isNumber(item.arrivalTime) ? DateFormat.DATE_TIME_ISO : DateFormat.DATE_ISO;

    const include = [
        "event",
        "units",
        "purchasedUnits",
        "resourceUsageCount",
        "guideCount",
        "noteCount",
        "capacity.reserved",
        "capacity.max",
        "customerName",
    ];

    if (isGranted(Permission.WAIVERS_VIEW)) {
        include.push("waiverCount", "waiverParticipantCount");
    }

    if (isGranted(Permission.MODIFY_RESOURCES_EQUIPMENT)) {
        include.push("resourceUsageCount");
    }

    if (isGranted(Permission.VIEW_RESOURCES_EQUIPMENT)) {
        include.push("units.open");
    }

    return {
        start: "arrivalDateTime" in item ? item.arrivalDateTime.format(startDateFormat) : "",
        product: item.product?.id,
        include: include.join(","),
        expand: "event,unit",
        item: item.id,
    };
};
